import {
    CSSProperties,
    ReactNode,
    useRef,
    useState,
    useImperativeHandle,
    useCallback,
    useEffect,
} from "react";
import type { Ref } from "react";
import { Scrollbars } from "react-custom-scrollbars-2";

interface InfiniteScrollProps {
    hasNextPage: boolean;
    loadMore: () => Promise<unknown>;
    direction?: "up" | "down";
    children: ReactNode;
    height?: number | string;
    threshold?: number;
    isFetchingMoreMessages?: boolean;
    className?: string;
    style?: CSSProperties;
}

export interface InfiniteScrollHandle {
    scrollToBottom: () => void;
    isAtBottom: boolean;
}

export const InfiniteScrollContainer = ({
    hasNextPage,
    loadMore,
    direction = "down",
    children,
    height = 400,
    threshold = 50,
    isFetchingMoreMessages,
    className,
    style,
    ref,
}: InfiniteScrollProps & { ref?: Ref<InfiniteScrollHandle> }) => {
    const scrollRef = useRef<Scrollbars>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const [isAtBottom, setIsAtBottom] = useState(true);
    const isAutoLoadingRef = useRef(false);

    const maybeLoadMore = useCallback(() => {
        if (!scrollRef.current) {
            return;
        }

        const scrollTop = scrollRef.current.getScrollTop();
        const scrollHeight = scrollRef.current.getScrollHeight();
        const clientHeight = scrollRef.current.getClientHeight();
        const canLoadMore = hasNextPage && !isFetchingMoreMessages;

        if (!canLoadMore) {
            return;
        }

        if (direction === "up") {
            if (scrollTop <= threshold) {
                const prevHeight = scrollHeight;
                isAutoLoadingRef.current = true;
                loadMore().finally(() => {
                    const newHeight = scrollRef.current?.getScrollHeight() ?? 0;
                    scrollRef.current?.scrollTop(
                        newHeight - prevHeight + scrollTop,
                    );
                    isAutoLoadingRef.current = false;
                });
            }
            return;
        }

        if (scrollTop + clientHeight >= scrollHeight - threshold) {
            isAutoLoadingRef.current = true;
            loadMore().finally(() => {
                isAutoLoadingRef.current = false;
            });
        }
    }, [hasNextPage, isFetchingMoreMessages, direction, threshold, loadMore]);

    const handleScroll = useCallback(() => {
        if (!scrollRef.current) {
            return;
        }

        const scrollTop = scrollRef.current.getScrollTop();
        const scrollHeight = scrollRef.current.getScrollHeight();
        const clientHeight = scrollRef.current.getClientHeight();

        setIsAtBottom(scrollTop + clientHeight >= scrollHeight - threshold);
        if (!isAutoLoadingRef.current) {
            maybeLoadMore();
        }
    }, [maybeLoadMore, threshold]);

    useEffect(() => {
        const contentEl = contentRef.current;
        if (!contentEl) {
            return;
        }

        const observer = new ResizeObserver(() => {
            if (!scrollRef.current || isAutoLoadingRef.current) {
                return;
            }

            const scrollHeight = scrollRef.current.getScrollHeight();
            const clientHeight = scrollRef.current.getClientHeight();
            const isNotScrollable = scrollHeight <= clientHeight + threshold;

            if (isNotScrollable) {
                maybeLoadMore();
            }
        });

        observer.observe(contentEl);

        return () => {
            observer.disconnect();
        };
    }, [maybeLoadMore, threshold]);

    useImperativeHandle(ref, () => ({
        scrollToBottom: () => {
            scrollRef.current?.scrollToBottom();
        },
        isAtBottom,
    }));

    return (
        <Scrollbars
            ref={scrollRef}
            autoHide
            style={{ ...style, height }}
            onScroll={handleScroll}
            className={className}
        >
            <div ref={contentRef}>{children}</div>
        </Scrollbars>
    );
};
